import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSliderModule } from '@angular/material/slider';
import { DriverSelectionAlgorithm } from '../shared/project/cards/driver-selection-algorithm';
import { DriverTypeFilter, DriverTypeFilterEditor } from '../shared/project/driver-types/driver-type-filter';
import { DriverTypeSelectionItem } from '../shared/project/driver-types/driver-type-selection-item';
import { DriverType } from '../shared/project/driver-types/driver-type';
import { AggregationSettings, UIAggregationSettings, UIFormCompAggregationSettingsItem } from '../shared/preaggregation/aggregation-settings';
import { UIAggregationMethod, UIAggregationType } from '../shared/preaggregation/aggregation-method';
import { AggregationSettingsHelper } from '../shared/preaggregation/aggregation-settings-helper';

export interface AlgoSettings {
    selectedAlgorithm: DriverSelectionAlgorithm;
    minDriverValue: number;
    minDriverValueIsAuto: boolean;
}

export interface SettingsModalData {
    viewId: string;
    tabId: string;
    outcomeName: string;
    algoSettings: AlgoSettings;
    driverTypeFilter: DriverTypeFilter;
    aggregationSettings: UIAggregationSettings;
}

@Component({
  selector: 'settings-modal',
  templateUrl: 'settings-modal.component.html',
  styleUrls: ['../import/general.scss', './settings-modal.component.scss']
})
export class SettingsModalComponent {
    private initialState: SettingsModalData;
    public model: SettingsModalData;
    public DriverSelectionAlgorithm = DriverSelectionAlgorithm;
    UIAggregationType: typeof UIAggregationType = UIAggregationType;
    public minDriverFocused = false;
    private driverTypeFilterEditor = new DriverTypeFilterEditor();

    constructor(@Inject(MAT_DIALOG_DATA) public data: SettingsModalData, private dialogRef: MatDialogRef<SettingsModalComponent>) {
        this.initialState = JSON.parse(JSON.stringify(data));
        this.model = data;
    }

    public save() {
        this.dialogRef.close(this.model);
    }

    /** The threshold is derived by the algorithm rather than set by hand. */
    public get minDriverValueIsAuto(): boolean {
        return this.model.algoSettings.minDriverValueIsAuto;
    }

    /** Any edit of the threshold, typed or via the x/: shortcuts, turns it into a manual one. */
    public setMinDriverValue(value: number) {
        this.model.algoSettings.minDriverValue = value;
        this.model.algoSettings.minDriverValueIsAuto = false;
    }

    /** Hands the threshold back to the algorithm; it is recalculated when the settings are saved. */
    public switchMinDriverValueToAuto() {
        this.model.algoSettings.minDriverValueIsAuto = true;
        if (this.initialState.algoSettings.minDriverValueIsAuto) {
            this.model.algoSettings.minDriverValue = this.initialState.algoSettings.minDriverValue;
        }
    }

    /** The main threshold field as shown while it is not focused, marked as auto-derived or user-defined. */
    public formatMinDriverValue(): string {
        const formatted = this.formatValue(this.model.algoSettings.minDriverValue);
        return `${formatted} ${this.minDriverValueIsAuto ? '(auto)' : '(manual)'}`;
    }

    public formatValue(value: number): string {
        if (!value) {
            return '0';
        }

        const absC = Math.abs(value);

        // suffix
        let suffix: string;
        if (absC < 1e-3) {
            suffix = 'E-6';
        } else if (absC < 1e-1) {
            suffix = '%';
        } else if (absC < 1e3) {
            suffix = '';
        } else if (absC < 1e6) {
            suffix = 'k';
        } else if (absC < 1e9) {
            suffix = 'm';
        } else {
            suffix = 'b';
        }

        // scaled value
        let factor: number;
        switch (suffix) {
            case 'E-6': factor = 1e6; break;
            case '%': factor = 1e2; break;
            case 'k': factor = 1e-3; break;
            case 'm': factor = 1e-6; break;
            case 'b': factor = 1e-9; break;
            default: factor = 1; break;
        }
        const h = value * factor;
        const i = Math.pow(10, Math.floor(Math.log10(Math.abs(h) / 5)));
        const j = Math.abs(h / i) >= 10 ? i : i / 2;
        const k = Math.round(h / j) * j;

        // Format with "#,##0.00", then strip trailing zeros and optional decimal point
        const text = k
            .toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            .replace(/\.?0+$/, '');

        return text + suffix;
    }

    public getStep(value: number): number {
        if (!value) {
            return 1;
        }
        const i = Math.pow(10, Math.floor(Math.log10(Math.abs(value) / 5)));
        return Math.abs(value / i) >= 10 ? i : i / 2;
    }

    public reset() {
        this.model = JSON.parse(JSON.stringify(this.initialState));
    }

    public onAggregationMethodChange(formComp: UIFormCompAggregationSettingsItem,  newValue: UIAggregationType) {
        AggregationSettingsHelper.onAggregationMethodChange(formComp, newValue);
    }

    public onColumnToAvgChange(aggrMethod: UIAggregationMethod): void {
        AggregationSettingsHelper.onColumnToAvgChange(aggrMethod, this.model.aggregationSettings.formComps);
    }

    selectNextDriverTypeScopeVal(driverType: DriverType, formulaComponentIndex?: number): void {
        this.driverTypeFilterEditor.setNextScopeVal(this.model.driverTypeFilter, driverType, formulaComponentIndex);
    }

    setNextDriverTypeFilterViewVal(item: DriverTypeSelectionItem): void {
        this.driverTypeFilterEditor.setNextViewVal(this.model.driverTypeFilter, item);
    }


}
